import { showSnackbar } from "./globalSnackbar";

// 2026-08-25 · Handlers globales de errores JS no controlados:
//   • `unhandledrejection` — promises rechazadas sin `.catch`.
//     Ejemplo típico: un thunk Redux que hace `err.message` sobre un
//     `err` undefined y no lo protege.
//   • `error` — excepciones síncronas escapadas del árbol React (que
//     no fueron atrapadas por ningún ErrorBoundary).
//
// Objetivo: SIEMPRE mostrar un toast humano, nunca pantalla en blanco.
// Además, publicar el error al slice `errors` para que el monitor del
// API (`useApiMonitorPersist`) lo mande al batch `/api-monitor/batch`
// si el usuario tiene monitorización activa. Sin monitorización activa,
// el error queda solo local (nunca se pierde para debug en vivo del
// panel de errores del admin).
//
// El módulo se auto-registra al importarse una vez desde `App.js`.
// Idempotente por flag interno — si el HMR lo re-ejecuta, no duplica
// listeners.

let installed = false;

// Ruido benigno del navegador que NO representa un bug real.
// Se filtra ANTES de dedupear, loguear y reportar al monitor — así ni
// aparece en la consola ni consume una plaza del batch de errores.
//   • ResizeObserver loop … : warning conocido de Chromium (MUI,
//     react-virtualized, TinyMCE, etc. lo disparan). No es un error.
//   • Script error. : errores CORS de scripts de terceros sin info útil.
//   • Non-Error promise rejection captured : ruido de librerías Sentry-like.
//   • Fallos de transporte/handshake de SignalR: la propia librería relanza
//     el error dentro de `onmessage` del WebSocket (no hay forma de
//     capturarlo); el HubConnectionManager ya lo registra y reintenta. Antes
//     salía el aviso rojo "error inesperado" al camarero por un corte de red.
const IGNORED_MESSAGE_PATTERNS = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Non-Error promise rejection captured/i,
  /Server returned handshake error/i,
  /Error parsing handshake response/i,
  /Handshake was canceled/i,
  /reading 'send'/i,
  /WebSocket closed with status code/i,
  /Failed to start the HttpConnection before stop\(\) was called/i,
];

export function isIgnorableMessage(message) {
  if (!message) return false;
  return IGNORED_MESSAGE_PATTERNS.some((rx) => rx.test(message));
}

// Dedup: evita mostrar 20 toasts si algo pete en un bucle. Cache 5s.
const recentSig = new Map();
function isDuplicate(sig) {
  const now = Date.now();
  // Purge entradas viejas.
  for (const [k, t] of recentSig.entries()) {
    if (now - t > 5000) recentSig.delete(k);
  }
  if (recentSig.has(sig)) return true;
  recentSig.set(sig, now);
  return false;
}

function trunc(s, max = 2000) {
  if (!s) return "";
  const str = typeof s === "string" ? s : String(s);
  return str.length > max ? str.substring(0, max) + "…" : str;
}

function extractMessage(err) {
  if (!err) return "Error desconocido";
  if (typeof err === "string") return err;
  return err.message
    || err.reason?.message
    || err.error?.message
    || (typeof err.reason === "string" ? err.reason : null)
    || "Error desconocido";
}

function extractStack(err) {
  return err?.stack || err?.reason?.stack || err?.error?.stack || null;
}

function reportToMonitor(source, err) {
  // Publica al slice `errors` reutilizando la infraestructura ya
  // montada (`useApiMonitorPersist` recoge cada 30s y flushea al
  // backend si monitorización está ON). El import es dinámico para
  // evitar ciclos con el store durante el bootstrap.
  try {
    // eslint-disable-next-line global-require
    const { store } = require("src/redux/store");
    // eslint-disable-next-line global-require
    const { addErrors } = require("src/redux/slices/errors");
    const message = extractMessage(err);
    const stack = extractStack(err);
    store.dispatch(addErrors({
      method: "CLIENT_ERROR",
      url: (typeof window !== "undefined" && window.location?.pathname) || "",
      status: 0,
      duration: 0,
      requestBody: null,
      // Guardamos el stack en responseBody — es lo que más ayuda al
      // depurar (línea + columna del culpable). Truncado a 2000
      // chars como los demás bodies del monitor.
      responseBody: trunc(stack, 2000),
      message: trunc(message, 500),
      customMessage: `[${source}] ${trunc(message, 300)}`,
      traceId: null,
      startedAt: new Date().toISOString(),
    }));
  } catch { /* nunca romper por reporting */ }
}

function handleClientError(source, err) {
  const message = extractMessage(err);
  if (isIgnorableMessage(message)) return;
  const sig = `${source}:${message}`;
  if (isDuplicate(sig)) return;

  // Toast humano (no técnico) — el stack no aporta al camarero.
  try {
    showSnackbar(
      "Ha ocurrido un error inesperado. La operación pudo no completarse.",
      { variant: "error", key: `client-error-${Date.now()}`, autoHideDuration: 5000 },
    );
  } catch { /* snackbar ref no montada aún */ }

  // Log a consola con contexto — el dev tools lo capta igualmente
  // pero así queda con el prefijo del handler.
  try {
    // eslint-disable-next-line no-console
    console.error(`[globalErrorHandler:${source}]`, err);
  } catch { /* ignore */ }

  reportToMonitor(source, err);
}

export function installGlobalErrorHandler() {
  if (installed) return;
  if (typeof window === "undefined") return;
  installed = true;

  // Promises no capturadas — el caso más frecuente (thunks Redux con
  // `catch (err) { err.message }` sobre un err undefined).
  window.addEventListener("unhandledrejection", (event) => {
    handleClientError("unhandledRejection", event.reason);
    // Prevenimos el default log de Chrome (que sale rojo en consola sin
    // contexto). Ya logueamos nosotros con prefijo.
    try { event.preventDefault?.(); } catch { /* ignore */ }
  });

  // Errores síncronos que escapan de React. React 18 los propaga aquí
  // si no hay ErrorBoundary. Excluimos errores de recursos (img/script)
  // que sean `null` — no aportan y suelen ser 404s de imágenes.
  window.addEventListener("error", (event) => {
    // Filtro: eventos sin `message` reales suelen ser errores de
    // recursos (img.onerror, script.onerror) que no queremos capturar.
    if (!event?.message && !event?.error) return;
    handleClientError("windowError", event.error || event);
  });
}

export default installGlobalErrorHandler;
